import { objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.esm.js';
import { useCollator } from '@react-aria/i18n';
import { setInteractionModality } from '@react-aria/interactions';
import { useField } from '@react-aria/label';
import { useMenuTrigger } from '@react-aria/menu';
import { ListKeyboardDelegate, useTypeSelect } from '@react-aria/selection';
import { filterDOMProps, mergeProps, useId, chain } from '@react-aria/utils';
import { useMemo } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
var selectData = new WeakMap();

/**
 * Provides the behavior and accessibility implementation for a select component.
 * A select displays a collapsible list of options and allows a user to select one of them.
 * @param props - Props for the select.
 * @param state - State for the select, as returned by `useListState`.
 */
function useSelect(props, state, ref) {
  var keyboardDelegate = props.keyboardDelegate,
    isDisabled = props.isDisabled,
    isRequired = props.isRequired,
    name = props.name,
    _props$validationBeha = props.validationBehavior,
    validationBehavior = _props$validationBeha === void 0 ? 'aria' : _props$validationBeha;

  // By default, a KeyboardDelegate is provided which uses the DOM to query layout information (e.g. for page up/page down).
  // When virtualized, the layout object will be passed in as a prop and override this.
  var collator = useCollator({
    usage: 'search',
    sensitivity: 'base'
  });
  var delegate = useMemo(function () {
    return keyboardDelegate || new ListKeyboardDelegate(state.collection, state.disabledKeys,
    // @ts-ignore
    null, collator);
  }, [keyboardDelegate, state.collection, state.disabledKeys, collator]);
  var _useMenuTrigger = useMenuTrigger({
      isDisabled: isDisabled,
      type: 'listbox'
    },
    // @ts-ignore
    state, ref),
    menuTriggerProps = _useMenuTrigger.menuTriggerProps,
    menuProps = _useMenuTrigger.menuProps;
  var onKeyDown = function onKeyDown(e) {
    switch (e.key) {
      case 'ArrowLeft':
        {
          // prevent scrolling containers
          e.preventDefault();
          var key = state.selectedKey != null && !Array.isArray(state.selectedKey) ? delegate.getKeyAbove && delegate.getKeyAbove(state.selectedKey) : delegate.getFirstKey && delegate.getFirstKey();
          if (key) {
            state.setSelectedKey(key);
          }
          break;
        }
      case 'ArrowRight':
        {
          // prevent scrolling containers
          e.preventDefault();
          var _key = state.selectedKey != null && !Array.isArray(state.selectedKey) ? delegate.getKeyBelow && delegate.getKeyBelow(state.selectedKey) : delegate.getFirstKey && delegate.getFirstKey();
          if (_key) {
            state.setSelectedKey(_key);
          }
          break;
        }
    }
  };
  var _useTypeSelect = useTypeSelect({
      keyboardDelegate: delegate,
      selectionManager: state.selectionManager,
      onTypeSelect: function onTypeSelect(key) {
        state.setSelectedKey(key);
      }
    }),
    typeSelectProps = _useTypeSelect.typeSelectProps;
  var _state$displayValidat = state.displayValidation,
    isInvalid = _state$displayValidat.isInvalid,
    validationErrors = _state$displayValidat.validationErrors,
    validationDetails = _state$displayValidat.validationDetails;
  var _useField = useField(_objectSpread2(_objectSpread2({}, props), {}, {
      labelElementType: 'span',
      isInvalid: isInvalid,
      errorMessage: props.errorMessage || validationErrors
    })),
    labelProps = _useField.labelProps,
    fieldProps = _useField.fieldProps,
    descriptionProps = _useField.descriptionProps,
    errorMessageProps = _useField.errorMessageProps;
  typeSelectProps.onKeyDown = typeSelectProps.onKeyDownCapture;
  delete typeSelectProps.onKeyDownCapture;
  var domProps = filterDOMProps(props, {
    labelable: true
  });
  var triggerProps = mergeProps(typeSelectProps, menuTriggerProps, fieldProps);
  var valueId = useId();
  selectData.set(state, {
    isDisabled: isDisabled,
    isRequired: isRequired,
    name: name,
    validationBehavior: validationBehavior
  });
  return {
    labelProps: _objectSpread2(_objectSpread2({}, labelProps), {}, {
      onClick: function onClick() {
        if (!props.isDisabled) {
          var _ref$current;
          (_ref$current = ref.current) === null || _ref$current === void 0 || _ref$current.focus();

          // Show the focus ring so the user knows where focus went
          setInteractionModality('keyboard');
        }
      }
    }),
    // @ts-ignore
    triggerProps: mergeProps(domProps, _objectSpread2(_objectSpread2({}, triggerProps), {}, {
      isDisabled: isDisabled,
      onKeyDown: chain(triggerProps.onKeyDown, onKeyDown, props.onKeyDown),
      onKeyUp: props.onKeyUp,
      'aria-labelledby': [valueId, triggerProps['aria-labelledby'], triggerProps['aria-label'] && !triggerProps['aria-labelledby'] ? triggerProps.id : null].filter(Boolean).join(' '),
      onFocus: function onFocus(e) {
        if (state.isFocused) {
          return;
        }
        if (props.onFocus) {
          // @ts-ignore
          props.onFocus(e);
        }
        if (props.onFocusChange) {
          props.onFocusChange(true);
        }
        state.setFocused(true);
      },
      onBlur: function onBlur(e) {
        if (state.isOpen) {
          return;
        }
        if (props.onBlur) {
          // @ts-ignore
          props.onBlur(e);
        }
        if (props.onFocusChange) {
          props.onFocusChange(false);
        }
        state.setFocused(false);
      }
    })),
    valueProps: {
      id: valueId
    },
    menuProps: _objectSpread2(_objectSpread2({}, menuProps), {}, {
      autoFocus: state.focusStrategy || true,
      shouldSelectOnPressUp: true,
      shouldFocusOnHover: true,
      disallowEmptySelection: true,
      linkBehavior: 'selection',
      onBlur: function onBlur(e) {
        if (e.currentTarget.contains(e.relatedTarget)) {
          return;
        }
        if (props.onBlur) {
          props.onBlur(e);
        }
        if (props.onFocusChange) {
          props.onFocusChange(false);
        }
        state.setFocused(false);
      },
      'aria-labelledby': [fieldProps['aria-labelledby'], triggerProps['aria-label'] && !fieldProps['aria-labelledby'] ? triggerProps.id : null].filter(Boolean).join(' ')
    }),
    descriptionProps: descriptionProps,
    errorMessageProps: errorMessageProps,
    isInvalid: isInvalid,
    validationErrors: validationErrors,
    validationDetails: validationDetails
  };
}

export { selectData, useSelect };
//# sourceMappingURL=use-select.esm.js.map
